import { usePublishEvent } from '@/analytics/usePublishEvent';
import { notifyError } from '@/common/utils/notifyError';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import styles from '../social-signin.module.scss';
import { SocialAlert, useSocialAlerts } from '../SocialAlertsContext';
import { useSocialReducer } from '../useSocialReducer';
import { GooglePlaceholderButton } from './GooglePlaceholderButton';

const googleUrl = 'https://accounts.google.com/gsi/client';

export interface CredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleSignInParams {
  onCredentialResponse: (
    response: CredentialResponse,
    elementId?: string,
  ) => void;
  disabled?: boolean;
  loading?: boolean;
}

export const GoogleSignIn = ({
  loading = false,
  onCredentialResponse,
  disabled,
}: GoogleSignInParams) => {
  const publishEvent = usePublishEvent();
  const setSocialAlert = useSocialAlerts();

  const btnRef = useRef<HTMLDivElement>(null);

  const { scrState, dispatch } = useSocialReducer({
    loaded:
      typeof window !== 'undefined' && typeof window.google !== 'undefined',
    failed: false,
  });

  const [isOneTap, setIsOneTap] = useState(false);

  const handleScriptError = () => {
    publishEvent('GoogleSignInFailedScript', 'google-login');
    dispatch({ type: 'failed' });
  };

  useEffect(() => {
    if (scrState.loaded) {
      const onGoogleResponse = (response: CredentialResponse) => {
        const isButton = response.select_by?.startsWith('btn');
        setIsOneTap(!isButton);

        const elementId = isButton
          ? 'google-login'
          : response.select_by === 'auto'
            ? 'google-automatic-login'
            : 'google-onetap-login';
        onCredentialResponse(response, elementId);
      };

      try {
        window.google.accounts.id.initialize({
          client_id: process.env.GOOGLE_CLIENT_ID,
          callback: onGoogleResponse,
          auto_select: true,
          use_fedcm_for_prompt: true,
        });

        window.google.accounts.id.renderButton(btnRef.current!, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          logo_alignment: 'center',
          text: 'continue_with',
          type: 'standard',
          width: btnRef.current?.clientWidth,
          click_listener:
            /* istanbul ignore next */
            () => publishEvent('ButtonClick', 'google-continue-button'),
        });

        /* istanbul ignore next */
        window.google.accounts.id.prompt((n) => {
          if (n.isDismissedMoment()) {
            if (n.getDismissedReason() === 'credential_returned') {
              publishEvent(
                'GoogleOneTapLoginSuccessful',
                'google-onetap-login',
              );
            } else {
              publishEvent('GoogleOneTapDismissed', 'google-onetap-login', {
                reason: n.getDismissedReason(),
              });
            }
          }
        });
      } catch (error) {
        notifyError(error);
        handleScriptError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrState.loaded]);

  const showPlaceholder = !scrState.loaded || loading || disabled || isOneTap;

  return (
    <>
      <Script
        src={googleUrl}
        onLoad={() => dispatch({ type: 'loaded' })}
        onError={handleScriptError}
      />
      <div className={styles.btn_container}>
        <div className={styles.google_btn_container}>
          <div
            id="google-login"
            data-testid="google-login"
            ref={btnRef}
            hidden={showPlaceholder}
          />
          {showPlaceholder && (
            <GooglePlaceholderButton
              disabled={disabled || loading || !scrState.failed}
              loading={loading && !isOneTap}
              onPlaceholderClicked={() => setSocialAlert(SocialAlert.GOOGLE)}
            />
          )}
        </div>
      </div>
    </>
  );
};
